import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import arrow from "../assets/arrow.png";
import { url, ak } from "../pages/HomePage";

function ReportPage() {
  const location = useLocation();
  const state = location.state;

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectReason, setShowRejectReason] = useState(false);

  function formatDate(dateString) {
    if (!dateString) return "-";

    const dateStr = dateString.toString();
    const isUTC =
      dateStr.includes("Z") ||
      (dateStr.includes("GMT") && dateStr.includes("+"));

    const date = new Date(dateStr);

    if (isUTC) {
      const tzOffsetMinutes = date.getTimezoneOffset();
      const utcDate = new Date(
        date.getTime() + tzOffsetMinutes * 60 * 1000
      );

      const formattedDate = utcDate.toLocaleDateString("fi-FI");
      const formattedTime = utcDate
        .toLocaleTimeString("fi-FI")
        .slice(0, -3);

      return `${formattedDate} kello ${formattedTime}`;
    }

    const formattedDate = date.toLocaleDateString("fi-FI");
    const formattedTime = date.toLocaleTimeString("fi-FI").slice(0, -3);

    return `${formattedDate} kello ${formattedTime}`;
  }

  async function loadReport() {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        url + `/admin/gig/${state.id}/report` + ak,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Raportin tietojen hakeminen epäonnistui.");
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  async function reviewTime(action) {
    try {
      setReviewing(true);
      setError("");

      const options = {
        method: "POST",
        credentials: "include",
      };

      if (action === "reject") {
        options.headers = {
          "Content-Type": "application/json",
        };
        options.body = JSON.stringify({
          reason: rejectionReason.trim(),
        });
      }

      const response = await fetch(
        url + `/admin/gig/${state.id}/report/time/${action}` + ak,
        options
      );

      if (!response.ok) {
        throw new Error("Työajan käsittely epäonnistui.");
      }

      await loadReport();
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewing(false);
    }
  }

  function reviewStatusText(status) {
    switch (status) {
      case "pending":
        return "Odottaa käsittelyä";
      case "approved":
        return "Hyväksytty";
      case "rejected":
        return "Hylätty – sovittu aika jää voimaan";
      case "not_required":
        return "Ei aikamuutosta";
      default:
        return status || "-";
    }
  }

  return (
    <div className="w-full h-[90%] flex flex-col items-center justify-center relative">
      <img
        src={arrow}
        alt="takaisin"
        className="absolute h-6 px-8 mb-4 cursor-pointer top-16 left-10"
        onClick={() => window.history.back()}
      />

      <div className="w-3/4 h-auto card">
        <h1 className="text-2xl font-bold">Raportti</h1>
        <br />

        <div className="flex">
          <b>Otsikko: </b>
          <p className="pl-2">{state.title}</p>
        </div>

        <div className="flex">
          <b>Asiakas: </b>
          <p className="pl-2">{state.customer}</p>
        </div>

        {loading ? (
          <p className="mt-4">Haetaan raportin tietoja...</p>
        ) : error && !reportData ? (
          <p className="mt-4 text-red-600">{error}</p>
        ) : (
          <>
            <div className="mt-5">
              <h2 className="text-lg font-bold">Työaika</h2>

              <div className="mt-2">
                <b>Sovittu aika:</b>
                <p>
                  {formatDate(reportData.agreed_start)} –{" "}
                  {formatDate(reportData.agreed_end)}
                </p>
              </div>

              {reportData.time_change_requested && (
                <div className="mt-3">
                  <b>Työntekijän ilmoittama toteutunut aika:</b>
                  <p>
                    {formatDate(reportData.reported_start)} –{" "}
                    {formatDate(reportData.reported_end)}
                  </p>

                  <p className="mt-2">
                    <b>Tila: </b>
                    {reviewStatusText(reportData.review_status)}
                  </p>
                </div>
              )}

              {reportData.review_status === "pending" && (
                <div className="mt-4">
                  <div className="flex">
                    <button
                      disabled={reviewing}
                      className="w-32 h-12 mr-4 font-bold text-white rounded bg-main text-md drop-shadow-md disabled:opacity-50"
                      onClick={() => reviewTime("approve")}
                    >
                      Hyväksy
                    </button>

                    <button
                      disabled={reviewing}
                      className="w-32 h-12 font-bold text-white bg-red-600 rounded text-md drop-shadow-md disabled:opacity-50"
                      onClick={() => setShowRejectReason(true)}
                    >
                      Hylkää
                    </button>
                  </div>

                  {showRejectReason && (
                    <div className="mt-4">
                      <label className="block font-bold">
                        Hylkäyksen syy:
                      </label>

                      <textarea
                        value={rejectionReason}
                        maxLength={500}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full p-2 mt-2 border rounded"
                        rows={4}
                      />

                      <div className="flex mt-3">
                        <button
                          disabled={reviewing || !rejectionReason.trim()}
                          className="px-4 h-10 mr-3 font-bold text-white bg-red-600 rounded disabled:opacity-50"
                          onClick={() => reviewTime("reject")}
                        >
                          Vahvista hylkäys
                        </button>

                        <button
                          disabled={reviewing}
                          className="px-4 h-10 rounded border"
                          onClick={() => {
                            setShowRejectReason(false);
                            setRejectionReason("");
                          }}
                        >
                          Peruuta
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {reportData.review_status === "rejected" &&
                reportData.rejection_reason && (
                  <div className="mt-4">
                    <b>Hylkäyksen syy:</b>
                    <p>{reportData.rejection_reason}</p>
                  </div>
                )}

              <div className="mt-4">
                <b>Lopullinen työaika:</b>
                <p>
                  {formatDate(reportData.final_start)} –{" "}
                  {formatDate(reportData.final_end)}
                </p>
              </div>
            </div>

            {error && <p className="mt-4 text-red-600">{error}</p>}

            <div className="mt-6">
              <b>Työntekijän raportti:</b>
              <p
                dangerouslySetInnerHTML={{
                  __html: (reportData.report || "").replace(
                    /-newline-/g,
                    "<br/>"
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ReportPage;
